/**
 * Copyright Fitzba Technologies Inc. 2023
 *
 * This module exports a React component named 'ItemInfo'. This component displays detailed information about a specific item.
 * It includes the item's price, delivery options, description, and tags. It also includes action buttons for adding the item to a shopping list,
 * buying the item, and reserving the item.
 *
 * The component takes three props: 'item', 'onClickReserve', and 'isMobile'. 'item' is the item being displayed. 'onClickReserve' is a function
 * that is called when the 'Reserve' button is clicked. 'isMobile' is a boolean indicating whether the device is mobile or not.
 *
 * @author Yang Ming, James Nguyen
 * @version January 19th, 2024
 */

import React, { useEffect, useRef, useState } from 'react'
import ShoppingCartHook from '@hooks/ShoppingCart.hook'
import ItemPrice from '../../zearch/components/ItemPrice'
import { useDispatch } from 'react-redux'
import { setSearchTitle } from '@reducers/contentReducer'
import { useNavigate } from 'react-router-dom'
import ItemActionButton from './ItemActionButton'
import ItemTags from './ItemTags'
// import { useMediaQuery } from 'react-responsive'
import ItemDeliveryIconList from './ItemDeliveryIconList'
import ItemVariants from './ItemVariants'
import ShareButtons from './ShareButtons'
import MessageForm from 'pages/store/components/MessageForm'
import { Drawer } from 'antd'
import StoreIcons from 'pages/store/components/StoreIcons'

/**
 * A component that displays detailed information about a specific item.
 *
 * @param {Object} props - The props object.
 * @param {Object} props.item - The item being displayed.
 * @param {Function} props.onClickReserve - A function that is called when the 'Reserve' button is clicked.
 * @param {boolean} props.isMobile - A boolean indicating whether the device is mobile or not.
 * @returns {JSX.Element} The 'ItemInfo' component.
 */
export default function ServiceInfo({
  service,
  isMobile,
  userinfo
}) {
  // console.log('ItemInfo: ', selectedVariant)

  // const isTablet = useMediaQuery({ query: '(max-width: 1279px)' })
  const ref = useRef()
  // const [openMap, setOpenMap] = useState(true)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [selectedProvider, setSelectedProvider] = useState(null)

  function handleBookNow(serviceId) {
    if (selectedProvider === null) {
      alert('Please select a provider before booking.')
      return
    }
    const provider = service?.providers?.find(provider => provider._id === selectedProvider)
    if (!provider) {
      alert('Selected provider not found.')
      return
    }
    // Navigate to the booking page with the selected service and provider
    // window.location.href = provider?.book_url;
    // create a tag and open a new tab
    window.open(provider?.book_url, '_blank')
  }

  return (
    <div className='px-5 text-left smsize:px-0'>
      <div className='flex flex-row items-center gap-2 py-3 sm:gap-5'>
        <div
          className='flex flex-1 flex-row items-baseline gap-1 text-2xl leading-8 tracking-[1px] text-fitzba-black-static sm:text-3xl sm:leading-10'
          ref={ref}
        >
          {service?.price > 0 ? (
            <ItemPrice
              originalPrice={service?.compareAt}
              salePrice={service?.price}
            />
          ) : ''}
          {service?.duration > 0 && (
            <div className='text-[16px] text-[#ACACAC] font-light'>
              / {service?.duration} minutes
            </div>
          )}
        </div>
      </div>
      {/* Various */}
      {service?.providers?.length > 0 && (
        <div className='pb-[30px] flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <h3 className='text-[18px] uppercase font-[400]'>
              Providers
            </h3>
            <div className='flex gap-3 flex-wrap'>
              {service?.providers?.map((item, index) =>
              (
                <button
                  key={index}
                  className={`${selectedProvider === item?._id && 'border-[#000]'} 
                border-[2px] rounded-[5px] px-4 py-1 font-[300] cursor-pointer hover:border-[#000] whitespace-nowrap`}
                  onClick={() => {
                    setSelectedProvider(item?._id)
                    // console.log('Selected Provider: ', item)
                  }}
                >
                  {item?.display_name || item?.nick_name}
                </button>
              )
              )}
            </div>
          </div>
        </div>
      )}
      <div className='w-full flex justify-start items-center mt-5'>
        {service?.business_id === '648a2da78501e1debb0fa93f' ?
          <a href={`https://flirtcosmetics.zenoti.com/webstoreNew/services/223d7d44-e6d0-4c03-9841-e653d25d4d0c?serviceid=${service.pos_service_id}`} target="_blank" rel="noopener noreferrer" className="bg-[#202020] uppercase rounded-full text-center cursor-pointer hover:bg-fitzba-bright-gold-static text-white py-2 px-4 w-[200px]">
            Book Now
          </a>
          :
          <button
            onClick={() => handleBookNow(service._id)}
            type="button"
            disabled={selectedProvider === null}
            className="bg-[#202020] rounded-full cursor-pointer hover:bg-fitzba-bright-gold-static disabled:bg-[#A6A6A6] text-white py-2 px-4 w-[200px] uppercase">
            Book Now
          </button>
        }
      </div>
      <div className='my-6 w-full border-b border-[#A6A6A6] xl:hidden' />
      <div
        className='mt-12 font-light'
        dangerouslySetInnerHTML={{
          __html: service?.description || 'No Description yet',
        }}
      />
      <ItemTags
        tags={service?.tags}
        dispatch={dispatch}
        setSearchTitle={setSearchTitle}
        navigate={navigate}
      />
      <ShareButtons />
    </div>
  )
}
